import { CalendarEvent } from '../types/calendar';
import { Client } from '@microsoft/microsoft-graph-client';
import { azureBlobService } from './azureBlobService';

export class CalendarService {
    private static STORAGE_KEY = 'thesis-calendar-events';
    private static isLoading = false;
    private static loadPromise: Promise<CalendarEvent[]> | null = null;

    static getEvents(): CalendarEvent[] {
        // Return localStorage data synchronously for immediate UI rendering
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            if (stored) {
                const events = JSON.parse(stored);
                // Convert date strings back to Date objects
                return events.map((event: any) => ({
                    ...event,
                    start: new Date(event.start),
                    end: new Date(event.end),
                    createdAt: new Date(event.createdAt),
                    updatedAt: new Date(event.updatedAt),
                }));
            }
        } catch (error) {
            console.error('Error loading events from localStorage:', error);
        }
        return [];
    }

    static async getEventsAsync(): Promise<CalendarEvent[]> {
        // Prevent multiple simultaneous loads
        if (this.isLoading && this.loadPromise) {
            return this.loadPromise;
        }

        this.isLoading = true;
        this.loadPromise = this.loadEventsFromStorage();

        try {
            const events = await this.loadPromise;
            return events;
        } finally {
            this.isLoading = false;
            this.loadPromise = null;
        }
    }

    private static async loadEventsFromStorage(): Promise<CalendarEvent[]> {
        try {
            // Try to load from Azure Blob Storage first
            if (azureBlobService.isAvailable()) {
                const blobEvents = await azureBlobService.loadEvents();
                if (blobEvents) {
                    // Update localStorage cache
                    this.saveToLocalStorage(blobEvents);
                    return blobEvents;
                } else {
                    // Blob doesn't exist yet, initialize it with default events
                    console.log('Initializing Azure Blob Storage with default events...');
                    const defaultEvents: CalendarEvent[] = [];
                    // Try to save default events to Azure (async, don't wait)
                    this.saveToAzureBlob(defaultEvents).catch(error => {
                        console.warn('Failed to initialize Azure Blob Storage with default events:', error);
                    });
                    return defaultEvents;
                }
            }

            return [];


        } catch (error) {
            console.error('Error loading events from storage:', error);
            return [];
        }
    }



    static saveEvents(events: CalendarEvent[]): void {
        // Save to localStorage immediately for quick access
        this.saveToLocalStorage(events);

        // Asynchronously save to Azure Blob Storage
        this.saveToAzureBlob(events);
    }

    private static saveToLocalStorage(events: CalendarEvent[]): void {
        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(events));
        } catch (error) {
            console.error('Error saving events to localStorage:', error);
        }
    }

    private static async saveToAzureBlob(events: CalendarEvent[]): Promise<void> {
        try {
            if (azureBlobService.isAvailable()) {
                const success = await azureBlobService.saveEvents(events);
                if (!success) {
                    console.warn('Failed to save events to Azure Blob Storage, using localStorage only');
                }
            }
        } catch (error) {
            console.error('Error saving events to Azure Blob Storage:', error);
        }
    }

    static addEvent(event: Omit<CalendarEvent, 'id' | 'createdAt' | 'updatedAt'>): CalendarEvent {
        const events = this.getEvents();
        const newEvent: CalendarEvent = {
            ...event,
            id: this.generateId(),
            createdAt: new Date(),
            updatedAt: new Date(),
        };
        events.push(newEvent);
        this.saveEvents(events);
        return newEvent;
    }

    static updateEvent(id: string, updates: Partial<CalendarEvent>): CalendarEvent | null {
        const events = this.getEvents();
        const index = events.findIndex(event => event.id === id);
        if (index === -1) return null;

        // Prevent updating synced events
        if (events[index].isSynced) {
            console.warn(`Attempted to update synced event: ${id}`);
            return null;
        }

        const updatedEvent = {
            ...events[index],
            ...updates,
            updatedAt: new Date(),
        };
        events[index] = updatedEvent;
        this.saveEvents(events);
        return updatedEvent;
    }

    static deleteEvent(id: string): boolean {
        const events = this.getEvents();
        const eventToDelete = events.find(event => event.id === id);

        // Prevent deleting synced events
        if (eventToDelete && eventToDelete.isSynced) {
            console.warn(`Attempted to delete synced event: ${id}`);
            return false;
        }

        const filteredEvents = events.filter(event => event.id !== id);
        if (filteredEvents.length === events.length) return false;

        this.saveEvents(filteredEvents);
        return true;
    }

    static async addToOutlookCalendar(event: CalendarEvent, graphClient: Client): Promise<boolean> {
        try {
            const outlookEvent = {
                subject: event.title,
                body: {
                    contentType: 'text',
                    content: event.description,
                },
                start: {
                    dateTime: event.start.toISOString(),
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                },
                end: {
                    dateTime: event.end.toISOString(),
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                },
                location: event.location ? {
                    displayName: event.location,
                } : undefined,
                isAllDay: event.allDay || false,
            };

            await graphClient.api('/me/events').post(outlookEvent);
            return true;
        } catch (error) {
            console.error('Error adding to Outlook calendar:', error);
            return false;
        }
    }

    private static generateId(): string {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }



    static async refreshFromAzure(): Promise<CalendarEvent[]> {
        try {
            if (azureBlobService.isAvailable()) {
                const events = await azureBlobService.loadEvents();
                if (events) {
                    this.saveToLocalStorage(events);
                    return events;
                }
            }
            return this.getEvents();
        } catch (error) {
            console.error('Error refreshing from Azure:', error);
            return this.getEvents();
        }
    }

    static isAzureBlobAvailable(): boolean {
        return azureBlobService.isAvailable();
    }

    // Sync functionality
    static async triggerSmartsheetSync(): Promise<{ success: boolean; data?: any; error?: string }> {
        try {
            const functionUrl = process.env.REACT_APP_SMARTSHEET_SYNC_URL || "http://localhost:7071/api/smartsheetSync";

            if (!functionUrl) {
                return {
                    success: false,
                    error: 'Smartsheet sync function URL not configured'
                };
            }

            console.log('Triggering Smartsheet sync...');

            const response = await fetch(functionUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                return {
                    success: false,
                    error: `Sync failed: ${errorData.error || response.statusText}`
                };
            }

            const data = await response.json();
            console.log('Smartsheet sync completed:', data);

            // Refresh events from Azure after sync
            if (data.success) {
                await this.refreshFromAzure();
            }

            return {
                success: data.success,
                data
            };

        } catch (error) {
            console.error('Error triggering Smartsheet sync:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    }

    static getSyncStats(events: CalendarEvent[]): {
        total: number;
        synced: number;
        manual: number;
        syncSources: Record<string, number>;
    } {
        const syncSources: Record<string, number> = {};
        let synced = 0;
        let manual = 0;

        events.forEach(event => {
            if (event.isSynced && event.syncSource) {
                synced++;
                syncSources[event.syncSource] = (syncSources[event.syncSource] || 0) + 1;
            } else {
                manual++;
            }
        });

        return {
            total: events.length,
            synced,
            manual,
            syncSources
        };
    }

    static getSyncedEvents(events: CalendarEvent[]): CalendarEvent[] {
        return events.filter(event => event.isSynced);
    }

    static getManualEvents(events: CalendarEvent[]): CalendarEvent[] {
        return events.filter(event => !event.isSynced);
    }
}
